import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { 
  addToAttendanceQueue, 
  getPendingAttendance, 
  markAttendanceSynced,
  deletePendingAttendance,
  getCachedToken,
  cacheTodayAttendance
} from './db';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
export const API_URL = API_BASE; // Alias for public endpoints

const IS_NATIVE = Capacitor.isNativePlatform();

// R2 Public URL pattern to detect
const R2_PUBLIC_URL_PATTERN = /^https:\/\/pub-[a-z0-9]+\.r2\.dev\//;

/**
 * Convert R2 public URL to proxy URL to avoid SSL certificate issues.
 * @param {string} url - The original R2 URL or any image URL
 * @returns {string} - Proxied URL if R2, otherwise original URL
 */
export const getProxiedImageUrl = (url) => {
  if (!url) return null;
  
  // Check if it's an R2 public URL
  if (R2_PUBLIC_URL_PATTERN.test(url)) {
    // Extract the path after the domain
    const path = url.replace(R2_PUBLIC_URL_PATTERN, '');
    return `${API_BASE}/media/r2/${path}`;
  }
  
  // Return original URL for other sources (base64, other CDNs, etc.)
  return url;
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token and platform header
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token') || await getCachedToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Signal native app to backend so Turnstile is bypassed
    if (IS_NATIVE) {
      config.headers['X-App-Platform'] = 'capacitor';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Flag to prevent multiple redirects
let isRedirecting = false;

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Prevent multiple redirects
      if (isRedirecting) {
        return Promise.reject(error);
      }
      
      const isLoginPage = window.location.pathname === '/login';
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      const isAuthMeRequest = error.config?.url?.includes('/auth/me');
      
      // Clear auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Only redirect if not already on login page and not during login/verify request
      if (!isLoginPage && !isLoginRequest && !isAuthMeRequest) {
        isRedirecting = true;
        console.log('401 Unauthorized - redirecting to login');
        // Small delay to let other requests complete
        setTimeout(() => {
          window.location.replace('/login');
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const login = async (nip, password, turnstileToken = null) => {
  const response = await api.post('/auth/login', { 
    nip, 
    password,
    turnstile_token: turnstileToken 
  });
  return response.data;
};

export const register = async (userData) => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// Change Password API
export const changePassword = async (currentPassword, newPassword, confirmPassword) => {
  const response = await api.post('/users/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
    confirm_password: confirmPassword
  });
  return response.data;
};

// Face Verification APIs
export const registerFaceReference = async (data) => {
  const response = await api.post('/face/register', data);
  return response.data;
};

export const getFaceReference = async () => {
  const response = await api.get('/face/reference');
  return response.data;
};

export const deleteFaceReference = async () => {
  const response = await api.delete('/face/reference');
  return response.data;
};

// Admin Face Registration APIs
export const adminRegisterFace = async (data) => {
  const response = await api.post('/face/admin-register', data);
  return response.data;
};

export const adminGetFaceReference = async (userId) => {
  const response = await api.get(`/face/admin-reference/${userId}`);
  return response.data;
};

export const adminDeleteFaceReference = async (userId) => {
  const response = await api.delete(`/face/admin-reference/${userId}`);
  return response.data;
};

export const getUsersFaceStatus = async (sekolahId = null) => {
  const params = sekolahId ? `?sekolah_id=${sekolahId}` : '';
  const response = await api.get(`/users/face-status${params}`);
  return response.data;
};

// Kiosk Mode APIs
export const registerKioskDevice = async (data) => {
  const response = await api.post('/kiosk/register', data);
  return response.data;
};

export const getKioskDevices = async (sekolahId = null) => {
  const params = sekolahId ? `?sekolah_id=${sekolahId}` : '';
  const response = await api.get(`/kiosk/devices${params}`);
  return response.data;
};

export const deleteKioskDevice = async (deviceId) => {
  const response = await api.delete(`/kiosk/devices/${deviceId}`);
  return response.data;
};

// Kiosk Public APIs (no auth required)
export const verifyKioskDevice = async (deviceToken) => {
  const response = await axios.get(`${API_URL}/kiosk/verify/${deviceToken}`);
  return response.data;
};

export const submitKioskAbsensi = async (data) => {
  const response = await axios.post(`${API_URL}/kiosk/absensi`, data);
  return response.data;
};

export const getServerTime = async () => {
  const response = await api.get('/server-time');
  return response.data;
};

// School APIs
export const getSekolahList = async () => {
  const response = await api.get('/sekolah');
  return response.data;
};

export const getSekolah = async (id) => {
  const response = await api.get(`/sekolah/${id}`);
  return response.data;
};

export const getSekolahDetail = async (id) => {
  const response = await api.get(`/sekolah/${id}/detail`);
  return response.data;
};

export const createSekolah = async (data) => {
  const response = await api.post('/sekolah', data);
  return response.data;
};

export const updateSekolah = async (id, data) => {
  const response = await api.put(`/sekolah/${id}`, data);
  return response.data;
};

export const getJenjangList = async () => {
  const response = await api.get('/jenjang');
  return response.data;
};

// Instansi APIs
export const getInstansiList = async () => {
  const response = await api.get('/instansi');
  return response.data;
};

export const getInstansi = async (id) => {
  const response = await api.get(`/instansi/${id}`);
  return response.data;
};

export const getInstansiDetail = async (id) => {
  const response = await api.get(`/instansi/${id}/detail`);
  return response.data;
};

export const createInstansi = async (data) => {
  const response = await api.post('/instansi', data);
  return response.data;
};

export const updateInstansi = async (id, data) => {
  const response = await api.put(`/instansi/${id}`, data);
  return response.data;
};

export const deleteInstansi = async (id) => {
  const response = await api.delete(`/instansi/${id}`);
  return response.data;
};

export const getJenisInstansiList = async () => {
  const response = await api.get('/jenis-instansi');
  return response.data;
};

// Guru APIs
export const getGuruList = async (sekolahId = null, includeKepsek = false, includeTtu = false) => {
  const params = {};
  if (sekolahId) params.sekolah_id = sekolahId;
  if (includeKepsek) params.include_kepsek = true;
  if (includeTtu) params.include_ttu = true;
  const response = await api.get('/guru', { params });
  return response.data;
};

export const updateGuru = async (id, data) => {
  const response = await api.put(`/guru/${id}`, data);
  return response.data;
};

// Attendance APIs
export const createAbsensi = async (data, isOffline = false) => {
  if (isOffline || !navigator.onLine) {
    // Store in IndexedDB queue
    const queuedRecord = await addToAttendanceQueue(data);
    return { ...queuedRecord, queued: true };
  }
  
  const response = await api.post('/absensi', data);
  return response.data;
};

export const getTodayAttendanceStatus = async () => {
  const response = await api.get('/absensi/today-status');
  return response.data;
};

export const getAbsensiList = async (params = {}) => {
  const response = await api.get('/absensi', { params });
  return response.data;
};

export const getTodayAbsensi = async () => {
  const response = await api.get('/absensi/today');
  return response.data;
};

export const getTodayAbsensiForMap = async () => {
  const response = await api.get('/absensi/today-map');
  return response.data;
};

export const getAbsensiMapByDate = async (tanggal) => {
  const response = await api.get('/absensi/map-by-date', { params: { tanggal } });
  return response.data;
};

export const getRealtimeAbsensi = async () => {
  const response = await api.get('/absensi/realtime');
  return response.data;
};

// Sync offline attendance
export const syncPendingAttendance = async () => {
  if (!navigator.onLine) return { synced: 0, failed: 0 };
  
  const pending = await getPendingAttendance();
  let synced = 0;
  let failed = 0;
  
  for (const record of pending) {
    try {
      const { id, timestamp, synced: _, ...data } = record;
      data.offline_timestamp = timestamp;
      
      // Send to server
      const response = await api.post('/absensi', data);
      
      // Cache the successful response to attendanceHistory
      if (response.data?.id) {
        await cacheTodayAttendance(response.data);
      }
      
      // Remove from pending queue
      await deletePendingAttendance(id);
      synced++;
    } catch (error) {
      console.error('Sync failed for record:', record.id, error);
      failed++;
    }
  }
  
  return { synced, failed };
};

// Izin APIs
export const createIzin = async (data) => {
  const response = await api.post('/izin', data);
  return response.data;
};

export const getIzinList = async (orgType) => {
  const params = orgType ? { org_type: orgType } : {};
  const response = await api.get('/izin', { params });
  return response.data;
};

export const approveIzin = async (id) => {
  const response = await api.put(`/izin/${id}/approve`);
  return response.data;
};

export const rejectIzin = async (id) => {
  const response = await api.put(`/izin/${id}/reject`);
  return response.data;
};

export const getIzinDokumen = async (izinId) => {
  const response = await api.get(`/izin/${izinId}/dokumen`);
  return response.data;
};

// Report APIs
export const getLaporanKinerja = async (bulan, tahun, sekolahId = null, limit = null, offset = 0, search = null) => {
  const params = { bulan, tahun };
  if (sekolahId) params.sekolah_id = sekolahId;
  if (limit) params.limit = limit;
  if (offset) params.offset = offset;
  if (search) params.search = search;
  const response = await api.get('/laporan/kinerja', { params });
  return response.data;
};

export const getRekapSekolah = async (bulan, tahun, options = {}) => {
  const params = { bulan, tahun, ...options };
  const response = await api.get('/laporan/rekap-sekolah', { params });
  return response.data;
};

export const getKinerjaAggregate = async (bulan, tahun, sekolahId = null) => {
  const params = { bulan, tahun };
  if (sekolahId) params.sekolah_id = sekolahId;
  const response = await api.get('/laporan/kinerja-aggregate', { params });
  return response.data;
};

export const getLaporanIndividu = async (guruId, bulan, tahun) => {
  const response = await api.get(`/laporan/individu/${guruId}`, { params: { bulan, tahun } });
  return response.data;
};

// Instansi Report APIs
export const getLaporanKinerjaInstansi = async (bulan, tahun, instansiId = null, limit = null, offset = 0, search = null) => {
  const params = { bulan, tahun };
  if (instansiId) params.instansi_id = instansiId;
  if (limit) params.limit = limit;
  if (offset) params.offset = offset;
  if (search) params.search = search;
  const response = await api.get('/laporan-instansi/kinerja', { params });
  return response.data;
};

export const getKinerjaAggregateInstansi = async (bulan, tahun, instansiId = null) => {
  const params = { bulan, tahun };
  if (instansiId) params.instansi_id = instansiId;
  const response = await api.get('/laporan-instansi/aggregate', { params });
  return response.data;
};

export const getLaporanIndividuInstansi = async (pegawaiId, bulan, tahun) => {
  const response = await api.get(`/laporan-instansi/individu/${pegawaiId}`, { params: { bulan, tahun } });
  return response.data;
};

export const getExportAbsensiHarian = async (tanggal, sekolahId = null) => {
  const params = {};
  if (tanggal) params.tanggal = tanggal;
  if (sekolahId) params.sekolah_id = sekolahId;
  const response = await api.get('/laporan/export/absensi-harian', { params });
  return response.data;
};

// Dashboard Stats
export const getDashboardStats = async () => {
  const response = await api.get('/dashboard/stats');
  return response.data;
};

// Audit Logs
export const getAuditLogs = async (params = {}) => {
  const response = await api.get('/audit-logs', { params });
  return response.data;
};

// Seed Demo Data
export const seedDemoData = async () => {
  const response = await api.post('/seed-demo-data');
  return response.data;
};

// ================= FOTO KEGIATAN APIs =================

export const uploadFotoKegiatan = async (data) => {
  const response = await api.post('/foto-kegiatan', data);
  return response.data;
};

export const getFotoKegiatanList = async (params = {}) => {
  const response = await api.get('/foto-kegiatan', { params });
  return response.data;
};

export const getTodayFotoCount = async () => {
  const response = await api.get('/foto-kegiatan/today/count');
  return response.data;
};

// Sync pending offline foto kegiatan (with lock to prevent duplicate uploads)
let _fotoSyncInFlight = false;

export const syncPendingFotoKegiatan = async () => {
  if (!navigator.onLine) return { synced: 0, failed: 0 };
  if (_fotoSyncInFlight) return { synced: 0, failed: 0, skipped: true };

  _fotoSyncInFlight = true;
  try {
    const { getPendingFotoKegiatan, markFotoKegiatanSynced, deletePendingFotoKegiatan } = await import('./db');
    const pending = await getPendingFotoKegiatan();
    let synced = 0;
    let failed = 0;

    for (const record of pending) {
      try {
        await uploadFotoKegiatan({
          foto_base64: record.foto_base64,
          keterangan: record.keterangan || null,
        });
        // Delete after successful sync to free IndexedDB storage
        await deletePendingFotoKegiatan(record.id);
        synced++;
      } catch (error) {
        const status = error.response?.status;
        // 4xx errors (quota, time window) — remove the item, don't retry
        if (status && status >= 400 && status < 500) {
          console.warn(`Foto kegiatan rejected (${status}), removing from queue:`, error.response?.data?.detail);
          await deletePendingFotoKegiatan(record.id);
          failed++;
        } else {
          // 5xx or network error — keep for retry
          console.error('Failed to sync foto kegiatan (will retry):', error);
          failed++;
        }
      }
    }
    return { synced, failed, total: pending.length };
  } finally {
    _fotoSyncInFlight = false;
  }
};

// ================= TELEGRAM APIs =================

export const generateTelegramLink = async () => {
  const response = await api.post('/telegram/generate-link');
  return response.data;
};

export const getTelegramStatus = async () => {
  const response = await api.get('/telegram/status');
  return response.data;
};

export const unlinkTelegram = async () => {
  const response = await api.delete('/telegram/unlink');
  return response.data;
};

export const sendTestNotification = async () => {
  const response = await api.post('/telegram/send-test');
  return response.data;
};

// ================= PUSH NOTIFICATION APIs =================

export const subscribePushNotifications = async (subscription) => {
  const response = await api.post('/notifications/subscribe', subscription);
  return response.data;
};

export const unsubscribePushNotifications = async () => {
  const response = await api.delete('/notifications/unsubscribe');
  return response.data;
};

// ================= USER MANAGEMENT APIs =================

export const getUsersList = async (params = {}) => {
  const response = await api.get('/users', { params });
  return response.data;
};

export const getUser = async (id) => {
  const response = await api.get(`/users/${id}`);
  return response.data;
};

export const createUser = async (data) => {
  const response = await api.post('/users', data);
  return response.data;
};

export const updateUser = async (id, data) => {
  const response = await api.put(`/users/${id}`, data);
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await api.delete(`/users/${id}`);
  return response.data;
};

export const getRoles = async () => {
  const response = await api.get('/roles');
  return response.data;
};

export const getUserPermissions = async () => {
  const response = await api.get('/user-permissions');
  return response.data;
};

// ================= SYSTEM SETTINGS API =================

export const getTimezoneSettings = async () => {
  const response = await api.get('/system-settings/timezone');
  return response.data;
};

export const updateTimezoneSettings = async (timezone) => {
  const response = await api.put('/system-settings/timezone', { timezone });
  return response.data;
};

export const getTimezoneOptions = async () => {
  const response = await api.get('/system-settings/timezone/options');
  return response.data;
};

// ================= SECURITY ALERTS API =================

export const getSecurityAlerts = async (params = {}) => {
  const response = await api.get('/security-alerts', { params });
  return response.data;
};

export const getSecurityAlertsStats = async () => {
  const response = await api.get('/security-alerts/stats');
  return response.data;
};

export const getSecurityAlertDetail = async (alertId) => {
  const response = await api.get(`/security-alerts/${alertId}`);
  return response.data;
};

export const reviewSecurityAlert = async (alertId, data) => {
  const response = await api.put(`/security-alerts/${alertId}/review`, data);
  return response.data;
};

export const deleteSecurityAlert = async (alertId) => {
  const response = await api.delete(`/security-alerts/${alertId}`);
  return response.data;
};

export const getLivenessSkipReport = async (days = 30) => {
  const response = await api.get('/security-alerts/liveness-skip-report', { params: { days } });
  return response.data;
};

export const getLivenessStats = async (bulan, tahun, sekolahId = null) => {
  const params = {};
  if (bulan) params.bulan = bulan;
  if (tahun) params.tahun = tahun;
  if (sekolahId) params.sekolah_id = sekolahId;
  const response = await api.get('/security-alerts/liveness/stats', { params });
  return response.data;
};

// ================= SURAT TUGAS API =================

export const getSuratTugasList = async (params = {}) => {
  const response = await api.get('/surat-tugas', { params });
  return response.data;
};

export const getSuratTugasDetail = async (id) => {
  const response = await api.get(`/surat-tugas/${id}`);
  return response.data;
};

export const createSuratTugas = async (data) => {
  const response = await api.post('/surat-tugas', data);
  return response.data;
};

export const updateSuratTugas = async (id, data) => {
  const response = await api.put(`/surat-tugas/${id}`, data);
  return response.data;
};

export const deleteSuratTugas = async (id) => {
  const response = await api.delete(`/surat-tugas/${id}`);
  return response.data;
};

export const completeSuratTugas = async (id) => {
  const response = await api.post(`/surat-tugas/${id}/complete`);
  return response.data;
};

export const cancelSuratTugas = async (id) => {
  const response = await api.post(`/surat-tugas/${id}/cancel`);
  return response.data;
};

export const getActiveSuratTugas = async (guruId) => {
  const response = await api.get(`/surat-tugas/active/${guruId}`);
  return response.data;
};

// ================= APP VERSION API =================

export const getCurrentAppVersion = async () => {
  const response = await api.get('/app-version/current');
  return response.data;
};

export const checkForUpdates = async (clientVersion) => {
  const response = await api.get(`/app-version/check/${clientVersion}`);
  return response.data;
};

export const getVersionHistory = async (limit = 20) => {
  const response = await api.get('/app-version', { params: { limit } });
  return response.data;
};

export const createAppVersion = async (data) => {
  const response = await api.post('/app-version', data);
  return response.data;
};

export const updateAppVersion = async (id, data) => {
  const response = await api.put(`/app-version/${id}`, data);
  return response.data;
};

export const deleteAppVersion = async (id) => {
  const response = await api.delete(`/app-version/${id}`);
  return response.data;
};

// ===================== BULK IMPORT =====================

export const downloadSekolahTemplate = async () => {
  const response = await api.get('/bulk-import/template/sekolah', {
    responseType: 'blob'
  });
  return response.data;
};

export const downloadUsersTemplate = async () => {
  const response = await api.get('/bulk-import/template/users', {
    responseType: 'blob'
  });
  return response.data;
};

export const bulkImportSekolah = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/bulk-import/sekolah', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const bulkImportUsers = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/bulk-import/users', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export default api;
